import { AlertCircle, Loader2 } from "lucide-react";

import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTopic } from "@/hooks/admin/topics";
import type { TagResponse } from "@/types";

const STATUS_TONE: Record<string, BadgeTone> = {
  draft_generated: "neutral",
  needs_review: "warning",
  approved: "success",
  edited_approved: "success",
  rejected: "danger",
  archived: "neutral",
};

function humanize(value: string) {
  return value.replace(/_/g, " ");
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{value}</p>
    </div>
  );
}

function TagPills({ label, tags }: { label: string; tags: TagResponse[] }) {
  if (tags.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <StatusBadge key={t.id} tone="neutral">
            {t.display_name}
          </StatusBadge>
        ))}
      </div>
    </div>
  );
}

export function TopicDetailModal({
  topicId,
  open,
  onClose,
}: {
  topicId: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const { data: topic, isPending, isError, error } = useTopic(open ? topicId : undefined);

  const meta = [topic?.difficulty, topic?.time_mode, topic?.evidence_richness].filter(
    Boolean
  ) as TagResponse[];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="scrollbar-thin max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-8">{topic?.title ?? "Topic"}</DialogTitle>
          {topic && (
            <DialogDescription>
              <span className="inline-flex items-center gap-2">
                <StatusBadge tone={STATUS_TONE[topic.task_status] ?? "neutral"}>
                  {humanize(topic.task_status)}
                </StatusBadge>
                {topic.archetype_name && <span>{topic.archetype_name}</span>}
                <span>· used {topic.times_used}×</span>
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        {isPending && (
          <div className="flex items-center justify-center p-8 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" />
            {error instanceof Error ? error.message : "Failed to load topic"}
          </div>
        )}

        {topic && (
          <div className="flex flex-col gap-4">
            <TagPills label="Topic areas" tags={topic.topic_tags} />
            <TagPills label="Attributes" tags={meta} />
            <Field label="Description" value={topic.description} />
            <Field label="Context" value={topic.context} />
            <Field label="Core question" value={topic.core_question} />
            <Field label="Guidance" value={topic.guidance} />
            <Field label="Evidence hints" value={topic.evidence_hints} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
